import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  HistoryResponse,
  LeagueBundle,
  MatchupPair,
  MatchupsResponse,
  SeasonPlacement,
} from '../../shared/types';
import { Avatar, Empty, Legend, Loading, PageHead, Section, medalClass } from '../components/common';
import {
  useAllTransactions,
  useDrafts,
  useIncidents,
  useLineups,
  useMatchups,
  useScores,
} from '../lib/api';
import { fmtOdds, fmtPct, fmtPoints, fmtRecord, fmtRelative, ordinal } from '../lib/format';
import {
  topHighlights,
  tradeExtremes,
  type Highlight,
  type TradeExtremes,
  type TraderRow,
} from '../lib/highlights';
import { focusWeek, priorFinishes } from '../lib/home';
import { incidentBoard, incidentRows, windowLabel } from '../lib/incidents';
import { buildTeamViews, isPreseason, teamIndex, type TeamView } from '../lib/league';
import { buildPreview, type PreviewPair } from '../lib/preview';
import { analyseAll, fitMargins, pairGames } from '../lib/projections';
import {
  efficiencyBoard,
  efficiencyRows,
  flattenHistory,
  type RecordRow,
  type ValueKind,
} from '../lib/records';
import { managerDirectory, type ManagerRef } from '../lib/rivalries';
import { Link } from '../lib/router';
import {
  buildPlayerLineages,
  buildProduction,
  buildRosterMaps,
  buildTradeLedger,
} from '../lib/tradetree';

/** Live countdown to the rookie draft. Ticks once a second; stops at zero. */
function Countdown({ target }: { target: number }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (target <= Date.now()) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);

  const left = Math.max(0, target - now);
  if (left === 0) return <div className="pill pill-ember">Draft is live</div>;

  const days = Math.floor(left / 86_400_000);
  const hours = Math.floor((left % 86_400_000) / 3_600_000);
  const mins = Math.floor((left % 3_600_000) / 60_000);
  const secs = Math.floor((left % 60_000) / 1000);

  const units: Array<[number, string]> = [
    [days, 'days'],
    [hours, 'hrs'],
    [mins, 'min'],
    [secs, 'sec'],
  ];

  return (
    <div className="countdown">
      {units.map(([value, label]) => (
        <div className="cd-unit" key={label}>
          <div className="cd-num">{String(value).padStart(2, '0')}</div>
          <div className="cd-lab">{label}</div>
        </div>
      ))}
    </div>
  );
}

function DefendingChampion({ history }: { history: HistoryResponse | null }) {
  const last = history?.seasons[0];
  const champ = last?.placements[0];
  // 2nd place is decided by the winners bracket's p:1 match, so the runner-up
  // is by definition whoever lost the final.
  const runnerUp = last?.placements[1];

  if (!last || !champ) {
    return (
      <div className="card champ">
        <div className="champ-body">
          <div>
            <div className="eyebrow">Inaugural season</div>
            <div className="champ-name">No champion yet</div>
            <div className="champ-manager">First title is unclaimed.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card champ">
      <div className="champ-body">
        <Avatar id={champ.avatar} alt={champ.team_name ?? champ.display_name} large />
        <div className="champ-id">
          <div className="eyebrow">{last.season} Champion</div>
          <div className="champ-name">{champ.team_name ?? champ.display_name}</div>
          <div className="champ-manager">@{champ.display_name}</div>
          {runnerUp && (
            <div className="champ-final">
              Beat {runnerUp.team_name ?? runnerUp.display_name} in the final
            </div>
          )}
        </div>
        {/* Pushed to the trailing edge, where the trophy emoji used to sit. The
            card is a hero and was two-thirds empty without something out here. */}
        <dl className="champ-line">
          <div>
            <dt>Record</dt>
            <dd>{fmtRecord(champ.record)}</dd>
          </div>
          <div>
            <dt>Points for</dt>
            <dd>{fmtPoints(champ.record.pointsFor)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

/** Silver and bronze from the last completed season -- the champ card has gold. */
function Podium({ season, rest }: { season: string; rest: SeasonPlacement[] }) {
  return (
    <div className="card card-pad">
      {/* Places, not "Final Standings" -- 1st is in the champion card beside
          this, so an unqualified standings list that opens at 2 reads broken. */}
      <div className="eyebrow">
        {season} · Places {rest[0]?.place ?? 2}–{rest[rest.length - 1]?.place ?? 4}
      </div>
      <div style={{ marginTop: 8 }}>
        {rest.map((p) => (
          <div className="score-row" key={p.roster_id}>
            <span className={`rank ${medalClass(p.place)}`}>{p.place}</span>
            <Avatar id={p.avatar} alt={p.team_name ?? p.display_name} />
            <span className="score-name">{p.team_name ?? p.display_name}</span>
            <span className="score-side">{fmtRecord(p.record)}</span>
          </div>
        ))}
      </div>
      <div className="stat-note" style={{ marginTop: 10 }}>
        <Link to="/history" className="muted">
          Full league history
        </Link>
      </div>
    </div>
  );
}

/**
 * The week's headline numbers, pulled from the same matchup fetch the grid
 * below uses. Only meaningful once the week has actually been played.
 */
function WeekHigh({
  data,
  teams,
}: {
  data: MatchupsResponse;
  teams: Map<number, TeamView>;
}) {
  const sides = data.matchups.flatMap((m) => m.teams);
  const top = sides.reduce((best, s) => (s.points > best.points ? s : best), sides[0]);
  const name = (rosterId: number) => teams.get(rosterId)?.name ?? `Roster ${rosterId}`;

  const paired = data.matchups.filter((m) => m.teams.length === 2);
  const closest = paired.reduce<MatchupPair | null>((best, m) => {
    if (!best) return m;
    const gap = Math.abs(m.teams[0].points - m.teams[1].points);
    const bestGap = Math.abs(best.teams[0].points - best.teams[1].points);
    return gap < bestGap ? m : best;
  }, null);
  const gap = closest ? Math.abs(closest.teams[0].points - closest.teams[1].points) : 0;

  return (
    <div className="card card-pad">
      <div className="eyebrow">Week {data.week} high</div>
      <h3 style={{ fontSize: 26, fontFamily: 'var(--display)' }}>{fmtPoints(top.points)}</h3>
      <div className="stat-note">{name(top.rosterId)}</div>
      {closest && (
        <div className="stat-note" style={{ marginTop: 12 }}>
          <strong>Closest game:</strong>{' '}
          {gap === 0
            ? `${name(closest.teams[0].rosterId)} and ${name(closest.teams[1].rosterId)} tied`
            : `${name(closest.teams[0].rosterId)} by ${fmtPoints(gap)} over ${name(closest.teams[1].rosterId)}`}
        </div>
      )}
    </div>
  );
}

/**
 * One matchup, compact. Scores when the week has been played; the projected
 * totals and the odds that come with them when it has not and the committed
 * projections reach it; each side's standing otherwise -- an upcoming game with
 * two 0.00s next to it tells nobody anything.
 */
function MiniMatchup({
  pair,
  teams,
  played,
  note,
  forecast,
}: {
  pair: MatchupPair;
  teams: Map<number, TeamView>;
  played: boolean;
  note: (rosterId: number) => string | null;
  /** Null when the snapshot does not cover this week. */
  forecast: PreviewPair | null;
}) {
  // A pair can sit inside a "played" week and still have no score of its own --
  // playoff weeks pair only the teams still alive. Treat those as unplayed
  // rather than reporting a 0-0 tie.
  const scored = played && pair.teams.reduce((sum, s) => sum + s.points, 0) > 0;
  const decided = scored && pair.teams.length === 2 && pair.teams[0].points !== pair.teams[1].points;

  // A forecast only ever shows on a row with no score. `buildPreview` has
  // already refused any week its snapshot does not describe, so there is no
  // calendar test to repeat here.
  const sides = scored || !forecast ? null : new Map(forecast.sides.map((s) => [s.rosterId, s]));

  // A total with an unprojected starter is a FLOOR, not an estimate, so any
  // comparison between two of them is biased by an unknown amount -- and always
  // against the side with the gap. `buildPreview` already withholds the odds on
  // exactly this test; the favourite and the margin are the same claim in other
  // clothes, so they are withheld too rather than carrying a footnote a reader
  // cannot act on. Same rule as a trade's `spread`.
  const settled = !!sides && forecast !== null && forecast.sides.every((s) => s.unprojected === 0);

  // Which row to accent. Deliberately NOT the winner styling: `.score-win`
  // turns the number green, and a projection has no winner -- colouring the
  // higher total as one would assert a result nobody has played for.
  const favoured =
    settled && forecast && forecast.sides.length === 2 && (forecast.margin ?? 0) > 0
      ? [...forecast.sides].sort((a, b) => b.projected - a.projected)[0].rosterId
      : null;

  // An empty slot really does score nothing, so it is NOT a floor the way an
  // unprojected starter is -- but nothing on the row marks it either, and a
  // lineup nobody has finished would otherwise read as a quiet low total.
  const unfilled = forecast ? forecast.sides.reduce((t, s) => t + s.empty, 0) : 0;

  const foot: string[] = [];
  if (settled && forecast && forecast.margin !== null) {
    foot.push(favoured === null ? 'Projected even' : `Projected by ${fmtPoints(forecast.margin)}`);
  }
  if (sides && unfilled > 0) {
    foot.push(`${unfilled} slot${unfilled === 1 ? '' : 's'} unfilled`);
  }

  // Ordered by whatever the card is about: the score when there is one (the API
  // already sorts those high first), the projected total when there is not.
  // Left in roster order, a projected card printed the 10% underdog above the
  // 90% favourite -- which on a card whose played sibling puts the winner on
  // top reads as a result rather than as an arbitrary order.
  const rows = sides
    ? [...pair.teams].sort(
        (a, b) =>
          (sides.get(b.rosterId)?.projected ?? 0) - (sides.get(a.rosterId)?.projected ?? 0),
      )
    : pair.teams;

  return (
    <div className="card card-pad">
      {rows.map((side, i) => {
        const t = teams.get(side.rosterId);
        const fc = sides?.get(side.rosterId) ?? null;
        const label = note(side.rosterId);
        return (
          <div
            className={`score-row${decided && i === 0 ? ' score-win' : ''}${
              favoured === side.rosterId ? ' score-fav' : ''
            }`}
            key={side.rosterId}
          >
            <Avatar id={t?.avatar ?? null} alt={t?.name ?? ''} />
            <span className="score-name">{t?.name ?? `Roster ${side.rosterId}`}</span>
            {scored ? (
              <span className="score-pts tabnum">{fmtPoints(side.points)}</span>
            ) : fc ? (
              /* The odds and the total travel as ONE right-aligned wrapper.
                 Flexbox splits free space between multiple auto margins, so
                 giving each its own would drift them apart by half the row's
                 slack -- and by more as the team name shortens. */
              <span className="score-forecast">
                {fc.winProbability !== null && (
                  <span className="score-odds tabnum">{fmtOdds(fc.winProbability)}</span>
                )}
                <span className="score-fc tabnum">
                  {/* A floor, not an estimate: a starter with no projection is
                      unknown rather than zero, so the total can only rise. */}
                  {fc.unprojected > 0 && <span className="score-fc-approx">≥</span>}
                  {fmtPoints(fc.projected)}
                </span>
              </span>
            ) : (
              label && <span className="score-side">{label}</span>
            )}
          </div>
        );
      })}
      {pair.teams.length === 1 && (
        <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
          No opponent this week
        </div>
      )}
      {scored && pair.teams.length === 2 && (
        <div className="score-foot">
          <span>
            {decided
              ? `Won by ${fmtPoints(Math.abs(pair.teams[0].points - pair.teams[1].points))}`
              : 'Tied'}
          </span>
        </div>
      )}
      {foot.length > 0 && (
        <div className="score-foot">
          <span>{foot.join(' · ')}</span>
        </div>
      )}
    </div>
  );
}

/**
 * The record-book boards this page leads with, in display order.
 *
 * Titles are the boards' own, so clicking through to /records finds the card
 * under the same name. The hints are NOT: a board's hint has a full-width card
 * to sit in and restates the title there ("Highest Score" / "Most points, one
 * team, one week"), which at 271px wraps to two lines and says nothing. These
 * carry only the qualifier that separates one card from its neighbour --
 * without "One week" against "Full season", the first two cards are the same
 * question asked twice.
 */
// Doubles as the loading skeleton, so it has to hold every card the section
// renders and hold them in the same order `topHighlights` is asked for -- a
// mismatch shows as cards changing places as the data lands. Ordered by the
// span each record covers: one game, then one season, then across seasons.
const BOOK_CARDS: { key: string; title: string; hint: string; skeleton?: boolean }[] = [
  { key: 'high', title: 'Highest Score', hint: 'One week' },
  { key: 'blowout', title: 'Biggest Blowout', hint: 'Largest margin' },
  { key: 'unlucky', title: 'Most Points In A Loss', hint: 'Highest losing score' },
  { key: 'best-record', title: 'Best Season Record', hint: 'Incl. playoffs' },
  { key: 'season-points', title: 'Most Points In A Season', hint: 'Full season' },
  // 'Regular season' is not decoration: this board's points come from Sleeper's
  // `fpts`/`ppts`, which stop at the playoff cutoff, while the card directly
  // above it counts every game. Same manager, same season, two totals.
  { key: 'efficiency', title: 'Best Lineup Efficiency', hint: 'Regular season' },
  { key: 'win-streak', title: 'Longest Win Streak', hint: 'Across seasons' },
  // Last, and the only entry kept OUT of the skeleton. Every other board here
  // is answered by any league that has played a game; this one is empty until
  // the nightly sync happens to catch someone, which may be months. A
  // placeholder would promise a card that never arrives -- worse than the
  // cards-changing-places problem the shared list exists to prevent, because
  // it never resolves. It still lives in this list so the keys and the titles
  // cannot drift apart.
  {
    key: 'off-field',
    title: 'Most Off-Field Incidents',
    // Never rendered -- the card overrides it with the ledger's own window.
    // Kept so the entry has the same shape as its neighbours.
    hint: 'Suspensions and holdouts',
    skeleton: false,
  },
];

const bookHint = (key: string): string => BOOK_CARDS.find((c) => c.key === key)?.hint ?? '';

/** The record book's own rendering rule: the headline depends on the board. */
function headline(kind: ValueKind, row: RecordRow): string {
  switch (kind) {
    case 'points':
      return fmtPoints(row.value);
    case 'pct':
      return fmtPct(row.value, 1);
    case 'count':
      return String(row.value);
    case 'record':
      // The win percentage is the sort key, but "12-2" is the record.
      return row.detail ?? fmtPct(row.value, 1);
  }
}

/**
 * Shared shell so a loading card and a filled one occupy the same slot.
 *
 * These cards arrive on three different fetches, and a grid whose cells appear
 * one at a time reflows the page under the reader twice.
 */
function BookCard({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <article className="card card-pad record-card">
      <div className="record-head">
        <h3>{title}</h3>
        <span className="hint">{hint}</span>
      </div>
      {children}
    </article>
  );
}

function CardWaiting({ title, hint }: { title: string; hint: string }) {
  return (
    <BookCard title={title} hint={hint}>
      <div className="faint" style={{ fontSize: 13, padding: '14px 0' }}>
        Loading
      </div>
    </BookCard>
  );
}

/** One all-time record, compacted to its holder. */
function RecordHighlight({
  h,
  dir,
  hint,
}: {
  h: Highlight;
  dir: Map<string, ManagerRef>;
  // Overridable because one card's qualifier is DATA rather than a constant:
  // the off-field board is bounded by the night its ledger started, and a
  // static string here would either go stale or omit the bound entirely.
  hint?: string;
}) {
  const name = (id: string) => dir.get(id)?.name ?? 'Unknown';
  const value = headline(h.kind, h.row);
  // On a 'record' board the detail IS the headline ("11-5"), so printing it
  // below would just repeat the number -- the same guard the record book uses.
  const detail = h.row.detail === value ? null : h.row.detail;
  const when = `${h.row.season}${h.row.week != null ? ` \u00b7 Week ${h.row.week}` : ''}`;
  const also = h.tiedWith.map(name);
  // `tied` counts the source data while `tiedWith` comes off a five-row board,
  // so a wide tie has holders this card cannot name.
  const unnamed = h.tied - 1 - h.tiedWith.length;

  // The note's facts, assembled as a list rather than as nested ternaries.
  // Three optional pieces is where the inline form starts emitting a stray
  // leading separator. Naming the beaten manager is the whole point of a margin
  // card: `blowout` and `unlucky` carry an `opponentId` where the season and
  // streak boards do not, so one component covers both without a branch.
  const parts = [
    detail,
    h.row.opponentId ? `vs ${name(h.row.opponentId)}` : null,
    also.length > 0
      ? `Shared with ${also.join(', ')}${unnamed > 0 ? ` and ${unnamed} more` : ''}`
      : null,
  ].filter((x): x is string => Boolean(x));

  return (
    <BookCard title={h.title} hint={hint ?? bookHint(h.key)}>
      <div className="record-hero">
        <div className="record-value">{value}</div>
        <div className="record-holder">
          <Avatar id={dir.get(h.row.userId)?.avatar ?? null} alt={name(h.row.userId)} />
          <div style={{ minWidth: 0 }}>
            <div className="team-title">{name(h.row.userId)}</div>
            <div className="team-handle">
              {when}
              {h.row.isPlayoff && <span className="pill pill-ember tiny">playoff</span>}
            </div>
          </div>
        </div>
      </div>
      {parts.length > 0 && <div className="record-note">{parts.join(' \u00b7 ')}</div>}
    </BookCard>
  );
}

/** Signed points. Zero takes neither colour -- a green "+0.00" reads as a gain. */
function netClass(n: number): string {
  return n > 0 ? 'good' : n < 0 ? 'bad' : '';
}

const fmtNet = (n: number): string => `${n > 0 ? '+' : ''}${fmtPoints(n)}`;

/**
 * One end of the trade ledger.
 *
 * Both ends render through this rather than through a hero and a runner-up
 * row: they are opposite extremes of one table, not first and second place, so
 * giving the worst a smaller treatment ranked it as an also-ran. Sharing the
 * component is also what keeps them identical -- two hand-matched blocks drift
 * apart on the next edit, and nothing would fail when they do.
 *
 * Returns two cells rather than a row: the parent `.ledger-ends` grid owns the
 * value column so both ends measure it together, which is what keeps the
 * avatars in line when one number carries a "+" and the other a "-".
 */
function LedgerEnd({ row, dir }: { row: TraderRow; dir: Map<string, ManagerRef> }) {
  const who = dir.get(row.userId);
  const name = who?.name ?? 'Unknown';

  return (
    <>
      <div className={`record-value tabnum ${netClass(row.net)}`}>{fmtNet(row.net)}</div>
      <div className="record-holder">
        <Avatar id={who?.avatar ?? null} alt={name} />
        <div style={{ minWidth: 0 }}>
          <div className="team-title">{name}</div>
          <div className="team-handle">
            {row.trades} trade{row.trades === 1 ? '' : 's'}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * The two ends of the trade ledger.
 *
 * Deliberately reports its own basis: only trades whose every asset has had a
 * chance to score are counted, which in an offseason is a minority of them.
 */
function TradeHighlight({ ex, dir }: { ex: TradeExtremes; dir: Map<string, ManagerRef> }) {
  const title = 'Trade Ledger';
  const hint = 'Best and worst net';

  if (!ex.best || !ex.worst) {
    return (
      <BookCard title={title} hint={hint}>
        <div className="faint" style={{ fontSize: 13, padding: '14px 0' }}>
          {ex.pending > 0
            ? `No trade has run its course yet. ${ex.pending} still holding an asset.`
            : 'No trades yet.'}
        </div>
      </BookCard>
    );
  }

  return (
    <BookCard title={title} hint={hint}>
      <div className="ledger-ends">
        <LedgerEnd row={ex.best} dir={dir} />
        <LedgerEnd row={ex.worst} dir={dir} />
      </div>

      <div className="record-note">
        {ex.counted} of {ex.counted + ex.pending} trades settled
      </div>
    </BookCard>
  );
}

export default function Home({
  bundle,
  history,
  historyLoading,
}: {
  bundle: LeagueBundle;
  history: HistoryResponse | null;
  /**
   * Whether the league-year walk is still in flight.
   *
   * The all-time cards below need every season's id, and `history` is null
   * both while loading and on an inaugural league. Without the distinction the
   * cards would render "not enough games" off the current season alone and
   * then correct themselves -- the failure mode `inauguralSeason` exists to
   * avoid in the masthead.
   */
  historyLoading: boolean;
}) {
  const teams = useMemo(() => buildTeamViews(bundle), [bundle]);
  const byRoster = useMemo(() => teamIndex(teams), [teams]);
  const preseason = isPreseason(bundle);

  const prior = useMemo(() => priorFinishes(bundle, history), [bundle, history]);

  const week = focusWeek(bundle);
  const matchups = useMatchups(bundle.league.league_id, week);
  const played = matchups.data?.played ?? false;

  const draftPending =
    bundle.draft && bundle.draft.status !== 'complete' && bundle.draft.startTime
      ? bundle.draft
      : null;

  const lastSeason = history?.seasons[0] ?? null;

  // Every league year, newest first. The Worker serves one season per call to
  // stay inside its subrequest budget, so the fan-out belongs here -- the same
  // shape /records and /transactions use, and `shared()` dedupes it across
  // them, so arriving here first costs a later page nothing.
  const leagueIds = useMemo(() => {
    const ids = [bundle.league.league_id];
    for (const season of history?.seasons ?? []) ids.push(season.league_id);
    return ids;
  }, [bundle.league.league_id, history]);

  // Held back until the walk lands: fetching now would ask for this season
  // alone and answer every all-time question with it.
  const scores = useScores(historyLoading ? [] : leagueIds);
  const txns = useAllTransactions(historyLoading ? [] : leagueIds);
  const drafts = useDrafts();
  const lineups = useLineups();
  const incidents = useIncidents();
  const dir = useMemo(() => managerDirectory(bundle, history), [bundle, history]);

  // The lineup ledger does double duty for the matchup rows above and is
  // optional for both: it carries the committed projections for the upcoming
  // week, and it is what the margin model is fitted from. A failure to load
  // simply means no forecast, and the rows fall back to each side's standing.
  //
  // The fit is always taken over EVERY game on record, never over the week on
  // screen -- the relationship between a projected margin and a real one is a
  // property of the league, not of these five games.
  const fit = useMemo(() => fitMargins(pairGames(analyseAll(lineups.data))), [lineups.data]);

  const preview = useMemo(
    () => (matchups.data ? buildPreview(matchups.data, lineups.data?.upcoming, fit) : []),
    [matchups.data, lineups.data, fit],
  );
  const forecasts = useMemo(() => new Map(preview.map((p) => [p.matchupId, p])), [preview]);

  // Efficiency is the one card here that /api/scores cannot answer: a `Game`
  // carries team totals only, and the optimal-lineup total lives on
  // `TeamRecord`. So the board is built here and handed to `topHighlights`
  // rather than found inside it -- with its uncapped rows alongside, which is
  // what its tie count has to be taken over.
  //
  // The incident ledger arrives the same way and for a stronger version of the
  // same reason: it is not derived from league play at all, it is a build-time
  // observation log. Its rows are uncapped here for the tie count, which on a
  // small-integer board is the one that actually gets exercised.
  const extraBoards = useMemo(
    () => [
      { board: efficiencyBoard(bundle, history), all: efficiencyRows(bundle, history) },
      { board: incidentBoard(incidents.data), all: incidentRows(incidents.data) },
    ],
    [bundle, history, incidents.data],
  );

  const highlights = useMemo(
    () =>
      topHighlights(
        flattenHistory(scores.data ?? [], true),
        BOOK_CARDS.map((c) => c.key),
        extraBoards,
      ),
    [scores.data, extraBoards],
  );

  const ledger = useMemo(() => {
    if (!txns.data) return null;
    const production = buildProduction(lineups.data);
    return buildTradeLedger(
      txns.data,
      drafts.data,
      buildRosterMaps(bundle, history, txns.data),
      production,
      buildPlayerLineages(txns.data, production),
    );
  }, [txns.data, drafts.data, lineups.data, bundle, history]);

  const trades = useMemo(() => (ledger ? tradeExtremes(ledger) : null), [ledger]);

  // The record cards ride on /api/scores; the trade card additionally needs the
  // draft board and the lineup ledger. Tracked apart so a slow lineups.json
  // does not hold up three cards that do not read it.
  const recordsPending = historyLoading || scores.loading;
  const tradesPending = historyLoading || txns.loading || drafts.loading || lineups.loading;

  // An inaugural season has nobody with a prior finish, so the matchup rows
  // carry no side note and the line explaining them has nothing to explain.
  const hasPrior = prior.size > 0;

  // What sits beside the champion card, in priority order. Computed rather than
  // decided inline so the single-column fallback below cannot disagree with it
  // and leave the hero half empty.
  const podiumRest = lastSeason?.placements.slice(1, 4) ?? [];
  const weekHigh = played && matchups.data && matchups.data.matchups.length > 0;
  const heroSecond = draftPending
    ? 'draft'
    : weekHigh
      ? 'week'
      : podiumRest.length > 0
        ? 'podium'
        : null;

  /** Right-hand context on an unplayed matchup: standing now, or last year's finish. */
  const sideNote = (rosterId: number): string | null => {
    if (!preseason) {
      const t = byRoster.get(rosterId);
      return t ? fmtRecord(t.record) : null;
    }
    const p = prior.get(rosterId);
    return p ? `${ordinal(p.place)} · ${fmtRecord(p.record)}` : null;
  };

  return (
    <>
      {/* The masthead wordmark is already the league name, and it sits ~150px
          above this. Titling the page with the season instead says something
          the header does not. */}
      <PageHead
        title={`${bundle.league.season} Season`}
        meta={[
          `${bundle.league.total_rosters}-team dynasty`,
          preseason ? `Kickoff week ${week}` : `Through week ${bundle.state.display_week}`,
          `Top ${bundle.league.playoff_teams} make the playoffs`,
        ]}
      />

      <div className={`hero${heroSecond ? '' : ' hero-solo'}`}>
        <DefendingChampion history={history} />
        {heroSecond === 'draft' && draftPending ? (
          <div className="card card-pad">
            <div className="eyebrow">Rookie Draft</div>
            <h3 style={{ fontSize: 22, textTransform: 'uppercase' }}>
              {bundle.draft?.rounds} rounds · {bundle.draft?.type}
            </h3>
            <Countdown target={draftPending.startTime as number} />
            <div className="stat-note" style={{ marginTop: 10 }}>
              {new Date(draftPending.startTime as number).toLocaleString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </div>
          </div>
        ) : heroSecond === 'week' && matchups.data ? (
          <WeekHigh data={matchups.data} teams={byRoster} />
        ) : heroSecond === 'podium' && lastSeason ? (
          <Podium season={lastSeason.season} rest={podiumRest} />
        ) : null}
      </div>

      <Section
        title={played ? `Week ${week} Scoreboard` : `Week ${week} Matchups`}
        hint={
          played ? (
            <Link to={`/recap/${bundle.league.season}/${week}`}>Week recap</Link>
          ) : (
            <Link to="/schedule">Full schedule</Link>
          )
        }
      >
        {matchups.loading && <Loading label={`Loading week ${week}`} />}
        {matchups.error && (
          <div className="card">
            <Empty>
              Could not load week {week}.{' '}
              <button className="linklike" onClick={matchups.reload}>
                Try again
              </button>
            </Empty>
          </div>
        )}
        {matchups.data && !matchups.loading && (
          <div className="grid grid-2 grid-start">
            {matchups.data.matchups.map((m) => (
              <MiniMatchup
                key={m.matchupId}
                pair={m}
                teams={byRoster}
                played={played}
                note={sideNote}
                forecast={forecasts.get(m.matchupId) ?? null}
              />
            ))}
          </div>
        )}
        {/* Only the entries describing something actually on screen. A glossary
            defining a symbol the page never renders is the same failure as a
            preamble written to fill a template slot. */}
        {matchups.data && !played && preview.length > 0 && (
          <Legend
            items={[
              {
                term: 'Projected',
                // Read off the ledger, which records what the feed returned in
                // `company`. Naming the supplier in the markup would keep
                // asserting it after the day Sleeper changes suppliers.
                def: `${lineups.data?.projectionSource ?? 'a generic PPR baseline'}, not this league's scoring`,
              },
              ...(preview.some((p) => p.sides.some((s) => s.winProbability !== null))
                ? [
                    {
                      term: 'Odds',
                      def: fit.fitted
                        ? `margin model fitted on ${fit.games} completed games, r = ${fit.r}`
                        : 'projections at face value; too few games to fit a model',
                    },
                  ]
                : []),
              ...(preview.some((p) => p.sides.some((s) => s.unprojected > 0))
                ? [{ term: '≥', def: 'a starter in that lineup has no projection' }]
                : []),
            ]}
          />
        )}
        {matchups.data && !played && preview.length === 0 && (!preseason || hasPrior) && (
          <p className="faint" style={{ fontSize: 12, marginTop: 10 }}>
            {preseason ? `${lastSeason?.season} finishes shown.` : 'Records to date shown.'}
          </p>
        )}
      </Section>

      <Section
        title="All-Time"
        hint={
          <>
            <Link to="/records">Record book</Link> · <Link to="/transactions">Trades</Link>
          </>
        }
      >
        <div className="grid grid-3 grid-start">
          {recordsPending
            ? BOOK_CARDS.filter((c) => c.skeleton !== false).map((c) => (
                <CardWaiting key={c.key} title={c.title} hint={c.hint} />
              ))
            : highlights.map((h) => (
                <RecordHighlight
                  key={h.key}
                  h={h}
                  dir={dir}
                  // The window is the caveat this card cannot be read without,
                  // so it takes the hint slot outright rather than sharing it
                  // with a restatement of the title.
                  hint={h.key === 'off-field' ? windowLabel(incidents.data) : undefined}
                />
              ))}

          {tradesPending ? (
            <CardWaiting title="Trade Ledger" hint="Best and worst net" />
          ) : (
            trades && <TradeHighlight ex={trades} dir={dir} />
          )}
        </div>

        {/* Both caveats are load-bearing rather than decorative: every figure
            on these cards is a running total, and the trade card is built from
            a subset it names on its own face. */}
        {!recordsPending && highlights.length === 0 && (
          <div className="card">
            <Empty>No games played yet.</Empty>
          </div>
        )}
        {(scores.error || txns.error) && (
          <p className="faint" style={{ fontSize: 12, marginTop: 10 }}>
            Some all-time data could not be loaded.{' '}
            <button
              className="linklike"
              onClick={() => {
                scores.reload();
                txns.reload();
              }}
            >
              Try again
            </button>
          </p>
        )}
      </Section>

      <div className="foot-inner" style={{ marginTop: 26, fontSize: 12, color: 'var(--text-faint)' }}>
        <span>League data refreshed {fmtRelative(bundle.fetchedAt)}.</span>
      </div>
    </>
  );
}
